import { useState, useEffect } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

export type RequestType = 'loading' | 'error' | 'data';

type useAsyncLoading = {
	type: 'loading',
	error: undefined,
	data: undefined,
	refresh: () => void,
}

type useAsyncError<E> = {
	type: 'error',
	error: E,
	data: undefined,
	refresh: () => void,
}

type useAsyncData<T> = {
	type: 'data',
	error: undefined,
	data: T,
	refresh: () => void,
}

export type useAsyncResponse<T, E> = useAsyncLoading | useAsyncError<E> | useAsyncData<T>;

export interface useAsyncOptions {
	/** Should the hook transition to the loading state after the first load. */
	suppressSubsequentLoadingState?: boolean
}

export type useAsyncFn<T> = (() => Promise<T>) | ((abortSignal: AbortSignal) => Promise<T>);

export default function useAsync<T = any, E = any>(
	fn: useAsyncFn<T>,
	deps?: readonly any[],
	options?: useAsyncOptions,
): useAsyncResponse<T, E> {
	const {
		suppressSubsequentLoadingState = false,
	} = options ?? {};

	const [data, setData] = useState<T>();
	const [error, setError] = useState<E>();
	const [type, setType] = useState<RequestType>('loading');
	const [refresh, setRefresh] = useState(false);

	useEffect(() => {
		const abortController = new AbortController();

		if (!suppressSubsequentLoadingState && !abortController.signal.aborted) {
			setType('loading');
		}

		fn(abortController.signal)
			.then(x => {
				if (abortController.signal.aborted) return;

				unstable_batchedUpdates(() => {
					setError(undefined);
					setData(x);
					setType('data');
				});
			})
			.catch(x => {
				if (abortController.signal.aborted) {
					return;
				}

				unstable_batchedUpdates(() => {
					setError(x);
					setData(undefined);
					setType('error');
				});
			});

		return () => abortController.abort();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, deps === undefined ? undefined : [refresh, ...deps]);

	// Cast to any since we know better than the compiler in this case. Since the function has an explicit return type
	// the function will be externally consistent.
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return {
		error,
		data,
		type,
		refresh: () => setRefresh(current => !current),
	} as any;
}
